'use client';

import Link from 'next/link';
import { useTranslation } from '@/context/LanguageContext';
import { EventItem } from '@/types/event';
import ShareSection from '@/components/shared/ShareSection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHome, faChevronRight, faTag, faCalendarPlus, faCalendarTimes, faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { faCalendar, faClock } from '@fortawesome/free-regular-svg-icons';

interface EventDetailContentProps {
    event: EventItem;
}

export default function EventDetailContent({ event }: EventDetailContentProps) {
    const { language } = useTranslation();

    const title = language === 'en' ? (event.title_en || event.title_idn) : (event.title_idn || event.title_en);
    const content = language === 'en' ? (event.content_en || event.content_idn) : (event.content_idn || event.content_en);

    const imageUrl = event.filename
        ? `https://unmul.ac.id/uploads/events/${event.filename}`
        : `/api/doServicesProxy?endpoint=web/file/event/${event.id}&isImage=true`;

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(dateString).toLocaleDateString(language === 'en' ? 'en-US' : 'id-ID', options);
    };

    const getEventTypeName = (type: number) => {
        switch (type) {
            case 1:
                return language === 'en' ? 'Research & Training' : 'Penelitian & Pelatihan';
            case 2:
                return language === 'en' ? 'Seminar & Webinar' : 'Seminar & Webinar';
            case 3:
                return language === 'en' ? 'Other Events' : 'Event Lainnya';
            default:
                return '';
        }
    };

    const eventTypeName = getEventTypeName(event.type);
    const shareTitle = language === 'en' ? 'Share This Event' : 'Bagikan Event Ini';
    const shareDesc = language === 'en' ? 'Share this event with your friends and colleagues' : 'Sebarkan informasi event ke teman dan kolega Anda';
    const backLabel = language === 'en' ? 'See Other Events' : 'Lihat Event Lainnya';

    return (
        <>
            {/* Hero Header Section */}
            <section className="pt-36 pb-16 bg-unmulGreen relative overflow-hidden">
                {/* SVG Ornaments Background */}
                <div className="absolute inset-0 opacity-10">
                    <svg className="absolute w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
                    </svg>
                    {/* Dots Pattern */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[url('/img/pattern-dots.svg')] opacity-20 transform rotate-45"></div>
                    {/* Circular Blurs */}
                    <div className="absolute top-10 right-10 w-48 h-48 bg-unmulYellow rounded-full blur-3xl opacity-30"></div>
                    <div className="absolute bottom-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl opacity-20"></div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    {/* Breadcrumbs */}
                    <nav className="flex items-center gap-2 text-sm text-white/80 mb-8">
                        <Link href="/" className="hover:text-white transition">
                            <FontAwesomeIcon icon={faHome} />
                        </Link>
                        <FontAwesomeIcon icon={faChevronRight} className="text-xs text-white/50" />
                        <span className="hover:text-white transition">Event</span>
                        <FontAwesomeIcon icon={faChevronRight} className="text-xs text-white/50" />
                        <span className="text-unmulYellow font-medium">{eventTypeName}</span>
                    </nav>

                    {/* Title */}
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-white leading-tight max-w-5xl">
                        {title}
                    </h1>

                    {/* Meta Info */}
                    <div className="flex flex-wrap items-center gap-6 mt-8 text-white/90">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                <FontAwesomeIcon icon={faCalendar} className="text-sm" />
                            </div>
                            <span className="font-medium">{formatDate(event.start_time)}</span>
                        </div>
                        {event.end_time && (
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                    <FontAwesomeIcon icon={faClock} className="text-sm" />
                                </div>
                                <span className="font-medium">
                                    {language === 'en' ? 'Until' : 'Sampai'} {formatDate(event.end_time)}
                                </span>
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                <FontAwesomeIcon icon={faTag} className="text-sm" />
                            </div>
                            <span className="font-medium">{eventTypeName}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Article Content Section */}
            <article className="py-12 bg-white">
                <div className="container mx-auto px-6">
                    {/* Featured Image - Full Width */}
                    <div className="max-w-6xl mx-auto mb-10">
                        <div className="rounded-2xl overflow-hidden shadow-xl">
                            <img
                                src={imageUrl}
                                alt={title}
                                className="w-full h-auto object-cover"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                }}
                            />
                        </div>
                    </div>

                    {/* Article Content - Wide */}
                    <div className="max-w-6xl mx-auto">
                        {/* Event Details Section */}
                        {(event.is_register === 1 || event.is_paid === 1) && (
                            <div className="bg-gray-50 rounded-xl p-6 mb-10 border border-gray-100">
                                <h3 className="text-xl font-serif font-bold text-unmulGreen mb-4">
                                    {language === 'en' ? 'Event Information' : 'Informasi Event'}
                                </h3>
                                <div className="grid md:grid-cols-2 gap-6">
                                    {event.is_register === 1 && event.reg_start_time && event.reg_end_time && (
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-3">
                                                {language === 'en' ? 'Registration' : 'Pendaftaran'}
                                            </h4>
                                            <div className="space-y-2 text-sm">
                                                <div className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCalendarPlus} className="text-unmulGreen mt-0.5" />
                                                    <div>
                                                        <span className="text-gray-600">{language === 'en' ? 'Opens:' : 'Dibuka:'}</span>
                                                        <span className="ml-2 font-medium">{formatDate(event.reg_start_time)}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCalendarTimes} className="text-unmulGreen mt-0.5" />
                                                    <div>
                                                        <span className="text-gray-600">{language === 'en' ? 'Closes:' : 'Ditutup:'}</span>
                                                        <span className="ml-2 font-medium">{formatDate(event.reg_end_time)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {event.is_paid === 1 && event.bank_name && (
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-3">
                                                {language === 'en' ? 'Payment Information' : 'Informasi Pembayaran'}
                                            </h4>
                                            <div className="space-y-2 text-sm">
                                                <div>
                                                    <span className="text-gray-600">{language === 'en' ? 'Bank:' : 'Bank:'}</span>
                                                    <span className="ml-2 font-medium">{event.bank_name}</span>
                                                </div>
                                                {event.bank_number && (
                                                    <div>
                                                        <span className="text-gray-600">{language === 'en' ? 'Account:' : 'Rekening:'}</span>
                                                        <span className="ml-2 font-medium font-mono">{event.bank_number}</span>
                                                    </div>
                                                )}
                                                {event.bank_person && (
                                                    <div>
                                                        <span className="text-gray-600">{language === 'en' ? 'Name:' : 'Atas Nama:'}</span>
                                                        <span className="ml-2 font-medium">{event.bank_person}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Main Content */}
                        <div
                            className={`prose prose-lg lg:prose-xl max-w-none text-gray-800
                        prose-headings:font-serif
                        prose-a:text-unmulGreen prose-a:no-underline hover:prose-a:underline
                        prose-img:rounded-xl prose-img:shadow-lg prose-img:mx-auto
                        prose-blockquote:border-l-unmulGreen prose-blockquote:bg-gray-50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg
                        
                        /* Explicit List Styling */
                        [&_ol]:list-decimal [&_ul]:list-disc
                        [&_ol]:pl-10 [&_ul]:pl-10
                        [&_li]:pl-2 [&_li]:mb-2
                        [&_li::marker]:text-unmulGreen [&_li::marker]:font-bold
                        
                        /* WYSIWYG Fixes */
                        [&_table]:w-full [&_table]:border-collapse [&_table]:my-6
                        [&_td]:border [&_td]:border-gray-200 [&_td]:p-3 [&_td]:align-top
                        [&_th]:border [&_th]:border-gray-200 [&_th]:p-3 [&_th]:bg-gray-50 [&_th]:text-left [&_th]:font-bold
                        [&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:rounded-xl [&_iframe]:shadow-md
                        [&_figure]:mx-auto [&_figure]:text-center
                        [&_figcaption]:text-center [&_figcaption]:text-sm [&_figcaption]:text-gray-500 [&_figcaption]:mt-2
                        break-words [&_p:empty]:min-h-[1em]`}
                            dangerouslySetInnerHTML={{ __html: content || '' }}
                        />

                        {/* Divider */}
                        <hr className="my-10 border-gray-200" />

                        {/* Share Section */}
                        <ShareSection
                            title={title}
                            url={`${process.env.NEXT_PUBLIC_ORIGIN || ''}/event/${event.slug}`}
                            shareTitle={shareTitle}
                            shareDesc={shareDesc}
                        />

                        {/* Navigation */}
                        <div className="mt-10 flex justify-center">
                            <Link
                                href="/event"
                                className="inline-flex items-center gap-2 text-unmulGreen font-semibold hover:gap-3 transition-all"
                            >
                                <FontAwesomeIcon icon={faArrowLeft} />
                                {backLabel}
                            </Link>
                        </div>
                    </div>
                </div>
            </article>
        </>
    );
}
